import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';

export interface ToastAction {
	label: string;
	onClick: () => void;
}

interface Toast {
	id: string;
	message: string;
	type: 'success' | 'error' | 'warning' | 'info' | 'progress';
	duration?: number;
	/** Never auto-hide; the X (or a programmatic dismiss) is the only way out.
	 * Action clicks do NOT dismiss a persistent toast. For long-running work
	 * (PD-12353 imports) paired with `upsert` so one toast updates in place. */
	persistent?: boolean;
	/** 0-100 renders a subtle progress bar under the message; a negative
	 * value renders an indeterminate (pulsing) bar. */
	progress?: number;
	/** Called when the user dismisses via the X. */
	onDismiss?: () => void;
	/** Internal: emitted by toast.dismiss() to remove an id. */
	remove?: boolean;
	/** Inline action button (e.g. "Undo", "Open"). Clicking it fires
	 * `action.onClick` and dismisses the toast. */
	action?: ToastAction;
	/** When set without `action`, the entire toast body is clickable.
	 * Typical use: "New message from X" → open the pop-out for that
	 * conversation. Click also dismisses the toast. */
	onClick?: () => void;
}

interface ToastActionOptions {
	duration?: number;
	onClick?: () => void;
	/** When provided, renders an inline action button with this label.
	 * When omitted, the whole toast body becomes clickable. */
	actionLabel?: string;
}

// Simple event emitter for toast notifications
class ToastEmitter {
	private _listeners: Array<(toast: Toast) => void> = [];
	private _idCounter = 0;

	public subscribe(listener: (toast: Toast) => void): () => void {
		this._listeners.push(listener);
		return (): void => {
			this._listeners = this._listeners.filter((l) => l !== listener);
		};
	}

	public emit(toast: Toast): void {
		this._listeners.forEach((listener) => {
			listener(toast);
		});
	}

	public generateId(): string {
		this._idCounter += 1;
		return `${Date.now()}-${this._idCounter}`;
	}
}

const toastEmitter = new ToastEmitter();

// Export a simple API for showing toasts
export const toast = {
	success: (message: string, duration?: number, action?: ToastAction): void => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'success',
			duration,
			action,
		});
	},
	error: (message: string, duration?: number, action?: ToastAction): void => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'error',
			duration,
			action,
		});
	},
	warning: (message: string, duration?: number, action?: ToastAction): void => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'warning',
			duration,
			action,
		});
	},
	info: (message: string, duration?: number, action?: ToastAction): void => {
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'info',
			duration,
			action,
		});
	},
	/**
	 * Insert-or-update a toast by a caller-chosen id. The workhorse for
	 * long-running background work: emit the same id as state changes and
	 * the one toast updates in place (type/message/progress can all change).
	 * Pair with `persistent: true` so it never hides on a timer.
	 */
	upsert: (config: {
		id: string;
		type?: Toast['type'];
		message: string;
		action?: ToastAction;
		persistent?: boolean;
		progress?: number;
		onDismiss?: () => void;
	}): void => {
		toastEmitter.emit({
			type: 'info',
			...config,
		});
	},
	/** Programmatically remove a toast by id (no-op if absent). */
	dismiss: (id: string): void => {
		toastEmitter.emit({ id, message: '', type: 'info', remove: true });
	},
	/**
	 * Info-level toast with a click action. Use for notifications that
	 * open something when clicked (new chat message, share request,
	 * etc.). With `actionLabel`, renders an inline button; without it,
	 * the whole toast body is clickable. Either way, firing the action
	 * dismisses the toast.
	 */
	action: (message: string, options: ToastActionOptions): void => {
		const { duration, onClick, actionLabel } = options;
		toastEmitter.emit({
			id: toastEmitter.generateId(),
			message,
			type: 'info',
			duration,
			// When a label is provided, surface it as the inline action
			// button (same field licensing/undo toasts use). Without a
			// label, fall back to the body-clickable mode by setting
			// `onClick` directly on the toast.
			action:
				actionLabel != null && onClick != null
					? { label: actionLabel, onClick }
					: undefined,
			onClick: actionLabel == null ? onClick : undefined,
		});
	},
};

export const ToastContainer: React.FC = () => {
	const { isMobile } = useResponsive();
	const [toasts, setToasts] = useState<Toast[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);

	// Headless UI v2's useOutsideClick registers pointerdown/pointerup listeners on
	// `document` in the capture phase. Bubble-phase stopPropagation on the toast element
	// fires too late to prevent it. By registering on `window` (which is above `document`
	// in the capture chain), we intercept these events before Headless UI sees them.
	useEffect(() => {
		const handler = (e: Event): void => {
			if (containerRef.current?.contains(e.target as Node) === true) {
				e.stopImmediatePropagation();
			}
		};

		window.addEventListener('pointerdown', handler, true);
		window.addEventListener('pointerup', handler, true);

		return (): void => {
			window.removeEventListener('pointerdown', handler, true);
			window.removeEventListener('pointerup', handler, true);
		};
	}, []);

	useEffect(() => {
		const unsubscribe = toastEmitter.subscribe((toast) => {
			if (toast.remove === true) {
				setToasts((prev) => prev.filter((t) => t.id !== toast.id));
				return;
			}
			// Upsert: an emit reusing an id updates that toast in place.
			setToasts((prev) =>
				prev.some((t) => t.id === toast.id)
					? prev.map((t) => (t.id === toast.id ? toast : t))
					: [...prev, toast],
			);

			// Auto remove after duration (default 5 seconds) - persistent
			// toasts have no timer; only the X or toast.dismiss() ends them.
			if (toast.persistent !== true) {
				// Explicit rather than ??/||: duration 0 keeps the old "fall back
				// to the default" behavior (persistent is the no-timer switch).
				const duration =
					toast.duration != null && toast.duration > 0 ? toast.duration : 5000;
				setTimeout(() => {
					setToasts((prev) => prev.filter((t) => t.id !== toast.id));
				}, duration);
			}
		});

		return unsubscribe;
	}, []);

	const removeToast = (id: string): void => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	/*
	 * A toast MUST be opaque.
	 *
	 * These used to use the `*-badge` fills, which are near-transparent by
	 * design (`--danger-badge-bg` is rgba(227,11,44,0.04) in light mode — 96%
	 * see-through). That is right for a badge sitting ON a card and wrong for
	 * something floating over arbitrary page content: whatever the toast covered
	 * read straight through it, and the page heading underneath collided with
	 * the message. It looks exactly like a z-index bug and is not one.
	 *
	 * `bg-raised` is the token for content floating above a surface (menus,
	 * popovers) and is solid in both themes. Colour still comes through the
	 * border, the icon and the text, which is where it belongs.
	 */
	const typeStyles = {
		success: cn('bg-raised text-success border border-success-tint'),
		error: cn('bg-raised text-danger border border-danger-tint'),
		warning: cn('bg-raised text-warning border border-warning-tint'),
		info: cn('bg-raised text-accent border border-accent-tint'),
		// Long-running/neutral: a plain raised card. text-primary is explicit
		// because crm-web sets no global body text color - inherit renders
		// black in dark mode.
		progress: cn(
			'bg-raised text-primary border border-black/10 dark:border-white/15',
		),
	};

	const icons = {
		success: (
			<svg
				className="w-5 h-5 text-success"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
					clipRule="evenodd"
				/>
			</svg>
		),
		error: (
			<svg
				className="w-5 h-5 text-danger"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
					clipRule="evenodd"
				/>
			</svg>
		),
		warning: (
			<svg
				className="w-5 h-5 text-warning"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
					clipRule="evenodd"
				/>
			</svg>
		),
		info: (
			<svg
				className="w-5 h-5 text-accent"
				fill="currentColor"
				viewBox="0 0 20 20"
			>
				<path
					fillRule="evenodd"
					d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
					clipRule="evenodd"
				/>
			</svg>
		),
		progress: (
			<svg
				className="w-5 h-5 text-secondary animate-spin"
				fill="none"
				viewBox="0 0 24 24"
			>
				<circle
					className="opacity-25"
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					strokeWidth="4"
				/>
				<path
					className="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
				/>
			</svg>
		),
	};

	return createPortal(
		<div
			ref={containerRef}
			className={cn(
				'fixed pointer-events-none',
				isMobile ? 'inset-x-4' : 'right-4',
			)}
			/* Rendered into document.body (see createPortal below) so the
			   z-index actually competes at the top level. Mounted inline it sat
			   inside the app-shell tree, where an ancestor's transform/opacity
			   creates a containing block — the z-index then only ranks it among
			   its siblings, and page chrome painted over the toast. */
			/*
			 * Offset below BOTH the device's top inset and the environment
			 * banner, using the same idiom as Sidebar.tsx.
			 *
			 * A flat `top-4` put toasts under the Dynamic Island on an iPhone 15
			 * Pro (top inset ~59px) AND on top of the staging banner, so the
			 * message was unreadable on both counts. The app shell's own top
			 * padding does not help: this container is `position: fixed`, so it
			 * escapes the shell and has to account for the inset itself.
			 *
			 * `--env-banner-height` is set by EnvironmentBanner only while the
			 * banner is actually rendered, so this collapses to just the inset
			 * plus the gap in production and after the banner is dismissed.
			 *
			 * Inline rather than a Tailwind arbitrary value because `env()` and
			 * `var()` inside one are exactly the sort of class that gets dropped
			 * silently at build time — and a dropped class here puts the toast
			 * back under the island with no error anywhere.
			 */
			style={{
				/*
				 * On a phone, toasts sit at the bottom, just above the 3.5rem tab
				 * bar. At the top they covered the right end of the page header,
				 * which on a phone is where the page's one main button lives
				 * (Save, Save day, Start): a rep who optimized a day and went to
				 * save it tapped the "Day optimized" toast instead.
				 */
				...(isMobile
					? {
							bottom:
								'calc(3.5rem + env(safe-area-inset-bottom, 0px) + 0.75rem)',
						}
					: {
							top: 'calc(var(--env-banner-height, 0px) + env(safe-area-inset-top, 0px) + 1rem)',
						}),
				/*
				 * Above the app's ceiling rather than equal to it. `z-[9999]` is
				 * shared by ~25 other overlays (modals, react-select menu portals,
				 * dropdowns), and among equal z-indexes the LAST element in DOM
				 * order paints on top. This portal attaches to <body> when the app
				 * shell mounts, so every dialog or menu portal created afterwards
				 * is a later sibling and wins the tie. One step above the ceiling
				 * removes the tie outright.
				 */
				zIndex: 10000,
			}}
		>
			<div className="space-y-2">
				{toasts.map((toast) => {
					// Body-click mode: only when there's a body onClick AND no inline
					// action button. If both are set, the action button wins as the
					// primary CTA (avoids ambiguity over what a body click should do).
					const isBodyClickable = toast.onClick != null && toast.action == null;
					const fireBody = (): void => {
						toast.onClick?.();
						removeToast(toast.id);
					};
					return (
						<div
							key={toast.id}
							// Prevent mousedown from reaching the non-modal Sidebar's
							// document-level bubble-phase outside-click handler
							onMouseDown={(e) => {
								e.stopPropagation();
							}}
							onClick={(e) => {
								if (
									isBodyClickable &&
									(e.target as HTMLElement).closest('button') == null
								) {
									fireBody();
								}
							}}
							className={cn(
								'pointer-events-auto flex items-start p-4 rounded-lg shadow-lg',
								'transform transition-all duration-300 ease-in-out',
								'translate-x-0 opacity-100',
								'dark:shadow-2xl dark:shadow-black/20',
								typeStyles[toast.type],
								isBodyClickable ? 'cursor-pointer hover:brightness-95' : '',
							)}
						>
							<div className="flex-shrink-0">{icons[toast.type]}</div>
							<div className="ml-3 flex-1 min-w-0">
								<Text className="text-sm font-medium whitespace-pre-line">
									{toast.message}
								</Text>
								{toast.progress != null && (
									<div className="mt-2 h-1 rounded-full overflow-hidden bg-black/10 dark:bg-white/15">
										<div
											className={cn(
												'h-full rounded-full bg-accent transition-all duration-500',
												toast.progress < 0 ? 'w-1/3 animate-pulse' : '',
											)}
											style={
												toast.progress >= 0
													? { width: `${Math.min(100, toast.progress)}%` }
													: undefined
											}
										/>
									</div>
								)}
								{toast.action != null && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											toast.action?.onClick();
											// A persistent toast survives its action (e.g. "View
											// progress" navigates but keeps reporting).
											if (toast.persistent !== true) {
												removeToast(toast.id);
											}
										}}
										className="mt-2 -ml-2 self-start flex items-center px-2 py-1 text-sm font-semibold rounded-md hover:bg-black/10 dark:hover:bg-white/15 focus:outline-none transition-colors underline underline-offset-2"
									>
										{toast.action.label}
									</button>
								)}
							</div>
							<button
								onClick={(e) => {
									e.stopPropagation();
									toast.onDismiss?.();
									removeToast(toast.id);
								}}
								className="ml-2 flex-shrink-0 inline-flex rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none transition-colors"
								aria-label="Close"
							>
								<svg
									className="w-4 h-4"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
										clipRule="evenodd"
									/>
								</svg>
							</button>
						</div>
					);
				})}
			</div>
		</div>,
		document.body,
	);
};
